import { FC, useState, useCallback, useMemo, useEffect } from 'react';
import { Input, Button, Drawer, Typography, theme, Tooltip, Progress } from 'antd';
import { EditOutlined, LinkOutlined, ReloadOutlined, SendOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import dayjs from 'dayjs';
import { ShippingTracker } from '#types';
import FlexBox from '@/components/atoms/FlexBox';
import FlexCol from '@/components/atoms/FlexCol';
import { usePdfExportContext } from '@/components/pdfExport/context/PdfExportContext';
import { useShippingTrackerUI } from './useShippingTrackerUI';
import { resolveTrackingUrlForShippingValue } from './resolveTrackingUrlForShippingValue';
import DetailedTrackingForm from './DetailedTrackingForm';
import { shippingCarriers } from './shippingCarriers';

const { Text } = Typography;

interface ShippingTrackerCompactUIProps {
    value?: string | ShippingTracker | null | undefined;
    onChange?: (value: ShippingTracker | null) => void;
    disabled?: boolean;
    status?: '' | 'error' | 'warning' | undefined;
    /** Stable id for aria (e.g. form field id). */
    fieldId?: string;
    [key: string]: any;
}

const ShippingTrackerCompactUI: FC<ShippingTrackerCompactUIProps> = ({
    value,
    onChange,
    disabled = false,
    status,
    fieldId,
    ...props
}) => {
    const {
        trackerValue,
        drawerOpen,
        setDrawerOpen,
        handleComponentChange,
        getCarrierInfo,
        getTrackingUrl,
        calculateProgress,
        refreshFromProvider,
        providerLabel,
        isRefreshing,
        refreshError,
    } = useShippingTrackerUI({ value, onChange });

    const { isPdfExportMode } = usePdfExportContext();
    const { token } = theme.useToken();
    const pdfExportUrl = resolveTrackingUrlForShippingValue(trackerValue);

    const components = trackerValue?.components;
    const trackingNumber = components?.trackingNumber || '';
    const carrier = getCarrierInfo(components?.carrierId || 'other');
    const pdfExportLinkText = [carrier?.name, trackingNumber].filter((x) => (x ?? '').toString().trim()).join(' ');
    const trackingUrl = getTrackingUrl(trackingNumber, components?.carrierId || 'other');
    const progress = calculateProgress(components?.currentStatus);
    const hasData = !!trackingNumber;

    const [localTrackingNumber, setLocalTrackingNumber] = useState(trackingNumber);

    // Sync local state when external value changes
    useEffect(() => {
        setLocalTrackingNumber(trackingNumber);
    }, [trackingNumber]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalTrackingNumber(e.target.value);
    }, []);

    const handleInputBlur = useCallback(() => {
        const trimmed = localTrackingNumber.trim();
        if (trimmed !== trackingNumber) {
            handleComponentChange({
                ...components,
                trackingNumber: trimmed,
                carrierId: components?.carrierId || 'other',
            });
        }
    }, [localTrackingNumber, trackingNumber, components, handleComponentChange]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleInputBlur();
        }
    }, [handleInputBlur]);

    const deliveryDateText = components?.deliveryDate instanceof Timestamp
        ? dayjs(components.deliveryDate.toDate()).format('MMM D')
        : '';

    const statusSuffix = useMemo(() => {
        if (!hasData) return null;

        const progressColor = (() => {
            if (progress === 100) return token.colorSuccess;
            if (progress >= 50) return token.colorPrimary;
            return token.colorWarning;
        })();

        return (
            <FlexBox gap={token.marginXXS}>
                {components?.currentStatus && (
                    <Tooltip title={`Status: ${components.currentStatus}`}>
                        <Progress
                            type="circle"
                            percent={progress}
                            size={18}
                            strokeColor={progressColor}
                            format={() => null}
                        />
                    </Tooltip>
                )}
                {deliveryDateText && (
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM, whiteSpace: 'nowrap' }}>
                        {deliveryDateText}
                    </Text>
                )}
                {trackingUrl && (
                    <Tooltip title={`Track on ${carrier?.name}`}>
                        <Button
                            type="link"
                            size="small"
                            icon={<LinkOutlined />}
                            href={trackingUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
                            aria-label="open-tracking-url"
                        />
                    </Tooltip>
                )}
            </FlexBox>
        );
    }, [hasData, components?.currentStatus, progress, deliveryDateText, trackingUrl, carrier?.name, token.colorSuccess, token.colorPrimary, token.colorWarning, token.marginXXS, token.fontSizeSM]);

    const inputPrefix = useMemo(() => {
        if (!hasData) return <SendOutlined style={{ color: token.colorTextSecondary }} />;

        const carrierInfo = shippingCarriers.find(c => c.id === components?.carrierId);
        if (carrierInfo && carrierInfo.id !== 'other') {
            return (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {carrierInfo.name}
                </Text>
            );
        }
        return <SendOutlined style={{ color: token.colorTextSecondary }} />;
    }, [hasData, components?.carrierId, token.colorTextSecondary, token.fontSizeSM]);

    return (
        <FlexCol style={{ width: '100%', ...props.style }} gap={isPdfExportMode && pdfExportUrl ? token.marginXXS : 0}>
            <FlexBox style={{ width: '100%' }}>
                <Input
                    value={localTrackingNumber}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    status={status}
                    variant="filled"
                    allowClear
                    placeholder="Enter tracking number..."
                    prefix={inputPrefix}
                    suffix={statusSuffix}
                    style={{ width: '100%' }}
                    aria-label="tracking-number-input"
                />
                <Button
                    type="text"
                    style={{ backgroundColor: token.colorBgBase }}
                    icon={<EditOutlined />}
                    disabled={disabled}
                    onClick={() => setDrawerOpen(true)}
                    aria-label="shipping-tracker-details-button"
                />
                {!disabled && hasData ? (
                    <Tooltip title={`Refresh from ${providerLabel}`}>
                        <Button
                            type="text"
                            style={{ backgroundColor: token.colorBgBase }}
                            icon={<ReloadOutlined />}
                            disabled={disabled || isRefreshing}
                            loading={isRefreshing}
                            onClick={() => void refreshFromProvider({ trackingNumber, carrierId: components?.carrierId || 'other', force: true })}
                            aria-label="shipping-tracker-refresh-button"
                        />
                    </Tooltip>
                ) : null}
                <Drawer
                    title="Shipping Tracker Details"
                    placement="right"
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    styles={{
                        wrapper: { width: 500 },
                        body: {
                            padding: token.padding,
                            overflowY: 'auto'
                        }
                    }}
                >
                    <DetailedTrackingForm
                        trackerValue={trackerValue}
                        disabled={disabled}
                        status={status}
                        onComponentChange={(comps) => {
                            handleComponentChange(comps);
                            setDrawerOpen(false);
                        }}
                        onRefresh={async ({ trackingNumber: tn, carrierId: cid, force }) => {
                            await refreshFromProvider({ trackingNumber: tn, carrierId: cid, force });
                            setDrawerOpen(false);
                        }}
                        isRefreshing={isRefreshing}
                        refreshError={refreshError}
                        onCancel={() => setDrawerOpen(false)}
                    />
                </Drawer>
            </FlexBox>
            {isPdfExportMode && pdfExportUrl && pdfExportLinkText ? (
                <Text style={{ fontSize: token.fontSizeSM }}>
                    <a
                        href={pdfExportUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: token.colorText }}
                        aria-label={fieldId ? `tracking-url-pdf-compact-${fieldId}` : 'tracking-url-pdf-compact'}
                    >
                        {pdfExportLinkText}
                    </a>
                </Text>
            ) : null}
        </FlexCol>
    );
};

export default ShippingTrackerCompactUI;
